import axios from "axios";
import * as React from "react";
import { useEffect, useState } from "react";
import HallSessions from "./HallSessions";

export const PRE_TRACKS: Array<ProgrammeSession["hall"]> = [
  "Track 1: Continuum",
  "Track 2: Reflexion",
  "Dinner Symposium",
];
export const DAY_1_TRACKS: Array<ProgrammeSession["hall"]> = [
  "Track 1 - Summit 1",
  "Track 2 - Zenith",
];
export const DAY_2_TRACKS: Array<ProgrammeSession["hall"]> = [
  "Track 1 - Summit 1",
  "Track 2 - Zenith",
];

const EVENT_DATES = [
  { dayNum: 0, dayName: "Pre-Congress Day", date: "(25th Apr 2025)" },
  { dayNum: 1, dayName: "Congress Day 1", date: "(26th Apr 2025)" },
  { dayNum: 2, dayName: "Congress Day 2", date: "(27th Apr 2025)" },
];

export const FACULTY_TYPE = [
  "Chairperson",
  "Citation",
  "Co-Chair",
  "Emcee",
  "Judge",
  "Guest Of Honor",
  "Speaker",
  "Adjudicator",
  "Moderator",
  "Facilitator",
  "Facilitators",
  "Panelist",
  "Case Presenters",
] as const;

export interface Faculty {
  id: number;
  honorific: string;
  fullName: string;
  avatar: string;
  shortProfile: string;
}

export interface FacultyWithType extends Faculty {
  facultyType: (typeof FACULTY_TYPE)[number];
}

export interface ProgrammeSession {
  id: number;
  title: string;
  desc?: string;
  startAt: string;
  endAt: string;
  date: string; // 11 JUN | SATURDAY
  time: string; // 10:00 to 11:00
  day: 1 | 2 | 3;
  hallOverwrite?: string;
  hall:
    | "Track 1: Continuum"
    | "Track 2: Reflexion"
    | "Track 1 - Summit 1"
    | "Track 2 - Zenith"
    | "Dinner Symposium";

  faculties: Array<FacultyWithType>;
  subsessions: Array<ProgrammeSubsession>;
  secondsTillStart: number;
  durationInSeconds: number;
  ended?: boolean;
}

export interface ProgrammeSubsession {
  id: number;
  title?: string;
  description?: string;
  time?: string;
  speakers: Array<FacultyWithType>; // This should use FacultyWithType, not just Faculty
}

const Programme = ({ defaultDay }) => {
  const [loading, setLoading] = useState(false);
  const [day, setDay] = useState(0);
  const [data, setData] = useState([]);

  const [activeLecture, setActiveLecture] = useState<ProgrammeSession["hall"]>(
    day === 0 ? PRE_TRACKS[0] : DAY_1_TRACKS[0]
  );

  useEffect(() => {
    setLoading(true);
    axios
      .get(`/api/programme_sessions?day=${day}`)
      .then((response) => {
        setData(response.data);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [day]);

  const dayClass = "btn btn-outline-red my-1 fw-bold me-2";

  const getTabColor = (active: boolean) => {
    return active ? "#D91A5D" : "#000";
  };

  if (loading) {
    return (
      <div className="loader">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container pb-5" id="programmeAgenda">
      <div className="filterBtns">
        {/* 1st row */}
        <div className="my-3 text-center">
          {EVENT_DATES.map(({ dayNum, dayName, date }) => (
            <button
              key={dayNum}
              className={`${dayNum === day && "active"} ${dayClass}`}
              onClick={() => {
                setDay(dayNum);

                if (dayNum === 0) {
                  setActiveLecture(PRE_TRACKS[0]);
                } else if (dayNum === 1) {
                  setActiveLecture(DAY_1_TRACKS[0]);
                } else if (dayNum === 2) {
                  setActiveLecture(DAY_2_TRACKS[0]);
                }
              }}
            >
              <div>{dayName}</div>
              <div>{date}</div>
            </button>
          ))}
        </div>
      </div>

      {/* 3rd row */}
      <div className="col-12">
        {day === 0 && (
          <div className="mt-5">
            <div className="d-block d-md-none">
              {PRE_TRACKS.map((hall) => (
                <button
                  key={hall}
                  className={`hallTabs fw-bold text-white`}
                  style={{
                    background: getTabColor(hall === activeLecture),
                    textTransform: "capitalize",
                  }}
                  onClick={() => {
                    setActiveLecture(hall);
                  }}
                >
                  {hall.replace("Lecture ", "")}
                </button>
              ))}
            </div>

            <div className="row gy-4">
              {PRE_TRACKS.map((hall) => (
                <div className="col-lg-4" key={hall}>
                  <HallSessions
                    hallName={hall}
                    activeHall={activeLecture}
                    programmes={data.filter(
                      (programme: ProgrammeSession) => programme.hall === hall
                    )}
                    getTabColor={getTabColor}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {day !== 0 && (
          <div className="mt-5">
            <div className="d-block d-md-none">
              {DAY_1_TRACKS.map((hall) => (
                <button
                  key={hall}
                  className={`hallTabs fw-bold text-white`}
                  style={{
                    background: getTabColor(hall === activeLecture),
                    textTransform: "capitalize",
                  }}
                  onClick={() => {
                    setActiveLecture(hall);
                  }}
                >
                  {hall.replace("Lecture ", "")}
                </button>
              ))}
            </div>

            <div className="row nogutters">
              {DAY_1_TRACKS.map((hall) => (
                <div className="col-md-6" key={hall}>
                  <HallSessions
                    hallName={hall}
                    activeHall={activeLecture}
                    programmes={data.filter(
                      (programme: ProgrammeSession) => programme.hall === hall
                    )}
                    getTabColor={getTabColor}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Programme;
